import { getActiveWorkflowByCategory, messageIsRaisedWorkflow, WORK_ITEM_TYPE_TASK_ID } from './reactionConfig';

/** Tasks created with +task on a workflow: titled, unmapped, messageId = workflow message. */

export const isUnmappedTask = (task) => !task?.mapTaskId || !String(task.mapTaskId).trim();

export const taskWorkflowMessageIds = (task) => {
  const ids = [];
  const seen = new Set();
  const add = (value) => {
    const id = value != null ? String(value).trim() : '';
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };
  const extras = task?.workflowMessageIds;
  if (extras && typeof extras.forEach === 'function') {
    extras.forEach(add);
  } else {
    add(extras);
  }
  add(task?.messageId);
  return ids;
};

const hasLinkedWorkflow = (task) => taskWorkflowMessageIds(task).length > 0;

export const isWorkflowShellTask = (task) => Boolean(
  task
  && isUnmappedTask(task)
  && hasLinkedWorkflow(task)
  && !(task.title || '').trim(),
);

export const explicitWorkflowMessageIds = (task) => {
  const ids = [];
  const seen = new Set();
  const add = (value) => {
    const id = value != null ? String(value).trim() : '';
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };
  const extras = task?.workflowMessageIds;
  if (extras && typeof extras.forEach === 'function') {
    extras.forEach(add);
  } else {
    add(extras);
  }
  return ids;
};

export const isWorkflowLinkedTask = (task) => Boolean(
  task
  && isUnmappedTask(task)
  && hasLinkedWorkflow(task)
  && (task.title || '').trim(),
);

/** Today's-list badge: only tasks actually mapped to a workflow, not tasks created from a regular chat. */
export const taskShowsWorkflowTag = (task, knownWorkflowIds) => {
  if (!task || !isUnmappedTask(task) || !(task.title || '').trim()) return false;
  const messageId = task.messageId != null ? String(task.messageId).trim() : '';
  const extras = explicitWorkflowMessageIds(task);
  const extraOnly = extras.filter((id) => id !== messageId);
  if (extraOnly.length > 0) return true;
  const known = knownWorkflowIds instanceof Set
    ? knownWorkflowIds
    : new Set((knownWorkflowIds || []).map((id) => String(id)));
  if (!known.size) return false;
  return taskWorkflowMessageIds(task).some((id) => known.has(id));
};

export const resolveTaskWorkflowChannelLabel = (task, {
  channels = [],
  directMessages = [],
  users = [],
  currentUserId,
  messagesByChannel = {},
  workflowItems = [],
} = {}) => {
  const ids = new Set(taskWorkflowMessageIds(task).map(String));
  if (!ids.size) return null;
  const wf = (workflowItems || []).find((item) => ids.has(String(item.messageId)));
  let channelId = task.channelId || wf?.channelId || null;
  if (!channelId && messagesByChannel) {
    Object.entries(messagesByChannel).some(([cid, list]) => {
      if ((list || []).some((msg) => ids.has(String(msg?.id)))) {
        channelId = cid;
        return true;
      }
      return false;
    });
  }
  const allChannels = [...channels, ...directMessages];
  const channel = allChannels.find((ch) => String(ch.id) === String(channelId));
  if (!channel && !wf?.channelName && !task.channelName) return null;
  const type = String(channel?.type || wf?.channelType || task.channelType || '').toUpperCase();
  if (type.includes('DIRECT')) {
    const memberIds = channel?.memberIds || wf?.channelMemberIds || [];
    const otherId = memberIds.find((id) => id && currentUserId && String(id) !== String(currentUserId));
    const peer = otherId ? users.find((user) => String(user.id) === String(otherId)) : null;
    const peerName = peer
      ? ([peer.firstName, peer.lastName].filter(Boolean).join(' ').trim() || peer.username)
      : (wf?.channelName || task.channelName || 'DM');
    return { kind: 'dm', label: `@${peerName}` };
  }
  const name = channel?.name || wf?.channelName || task.channelName;
  if (!name) return null;
  return { kind: 'channel', label: `#${name}` };
};

export const isWorkflowSourceTask = (task) => Boolean(
  task
  && isUnmappedTask(task)
  && hasLinkedWorkflow(task)
);

export const isWorkflowMappingParent = (task, workflowMessageId) => {
  const workflowId = workflowMessageId != null ? String(workflowMessageId).trim() : '';
  if (!task?.id || !workflowId || !isUnmappedTask(task)) return false;
  return taskWorkflowMessageIds(task).includes(workflowId);
};

export const uniqueTasksById = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((task) => {
    if (!task?.id) return;
    const existing = byId.get(task.id);
    byId.set(task.id, existing ? mergeTaskRecords(existing, task) : task);
  });
  return Array.from(byId.values());
};

export const taskDelegateIds = (task) => {
  const raw = task?.delegateId;
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((id) => String(id || '').trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    return raw.trim() ? [raw.trim()] : [];
  }
  if (typeof raw === 'object') {
    const ids = [];
    if (typeof raw.forEach === 'function') {
      raw.forEach((id) => {
        const value = String(id || '').trim();
        if (value) ids.push(value);
      });
      return ids;
    }
    if (typeof raw.length === 'number') {
      return Array.from(raw).map((id) => String(id || '').trim()).filter(Boolean);
    }
  }
  return [];
};

export const formatTaskAssigneeLabel = (task, users = [], currentUserId) => {
  const delegates = taskDelegateIds(task);
  const creatorId = task?.createdByUserId != null ? String(task.createdByUserId).trim() : '';
  const assignedIds = delegates.length ? delegates : (creatorId ? [creatorId] : []);
  if (!assignedIds.length) return '';
  const others = assignedIds.filter((id) => String(id) !== String(currentUserId || ''));
  const showIds = others.length ? others : assignedIds;
  const names = showIds.map((id) => {
    if (String(id) === String(currentUserId || '')) return 'You';
    const user = (users || []).find((item) => String(item?.id) === String(id));
    return [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
      || user?.username
      || user?.label
      || '';
  }).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  const extra = names.length - 1;
  return `${names[0]} + ${extra} other${extra === 1 ? '' : 's'}`;
};

const mergeTaskRecords = (existing, next) => {
  const merged = { ...existing, ...next };
  const nextDelegates = taskDelegateIds(next);
  const existingDelegates = taskDelegateIds(existing);
  if (nextDelegates.length) merged.delegateId = nextDelegates;
  else if (existingDelegates.length) merged.delegateId = existingDelegates;
  return merged;
};

export const isTaskLinkedToWorkflow = (task, workflowMessageId) => {
  const workflowId = workflowMessageId != null ? String(workflowMessageId).trim() : '';
  if (!task?.id || !workflowId || !isUnmappedTask(task)) return false;
  if (!(task.title || '').trim()) return false;
  return taskWorkflowMessageIds(task).includes(workflowId);
};

/** Linked tasks shown in the workflow popup, including auto-created journey rows. */
export const visibleWorkflowLinkedTasks = (tasks = [], workflowMessageId, fallbackTitle = '') => {
  const workflowId = workflowMessageId != null ? String(workflowMessageId).trim() : '';
  if (!workflowId) return [];
  return (Array.isArray(tasks) ? tasks : [])
    .filter((task) => (
      task?.id
      && isUnmappedTask(task)
      && taskWorkflowMessageIds(task).includes(workflowId)
    ))
    .map((task) => ({
      ...task,
      title: String(task?.title || '').trim() || String(fallbackTitle || '').trim() || 'Workflow task',
    }));
};

export const isWorkflowMessage = (msg) => {
  if (!msg) return false;
  const reactions = msg.reactions || [];
  if (messageIsRaisedWorkflow(reactions)) return true;
  const typeId = msg.workflowByCategory?.['work-item-type'];
  return Boolean(typeId && typeId !== WORK_ITEM_TYPE_TASK_ID);
};

export const messageToWorkflowItem = (msg) => {
  if (!msg) return null;
  const messageId = msg.id || msg.messageId;
  const channelId = msg.channelId;
  if (!messageId) return null;
  return {
    messageId,
    channelId,
    content: msg.content,
    contentHtml: msg.contentHtml || msg.content,
    workflowTitle: msg.workflowTitle,
    workflowDescription: msg.workflowDescription,
    workflowSpaceId: msg.workflowSpaceId,
    workflowSpaceName: msg.workflowSpaceName,
    workflowTrackId: msg.workflowTrackId,
    workflowTrackLink: msg.workflowTrackLink,
    workflowAssignedToUserId: msg.workflowAssignedToUserId,
    workflowAssignedToName: msg.workflowAssignedToName,
    workflowByCategory: msg.workflowByCategory || getActiveWorkflowByCategory(msg.reactions || []),
    customTags: msg.customTags || [],
    senderId: msg.senderId,
    senderName: msg.senderName,
    senderProfilePicture: msg.senderProfilePicture,
    createdAt: msg.createdAt || msg.timestamp,
    attachments: msg.attachments || [],
    attachmentGroupName: msg.attachmentGroupName || '',
    reactions: msg.reactions || [],
  };
};

export const workflowItemLabel = (item) => {
  const title = (item?.workflowTitle || '').trim();
  if (title) return title;
  const raw = item?.contentHtml || item?.content || '';
  if (!raw) return 'Workflow';
  if (typeof document === 'undefined') {
    return String(raw).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Workflow';
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = raw;
  return (tmp.textContent || tmp.innerText || '').trim().slice(0, 80) || 'Workflow';
};

export const isNamedSubtask = (task) => Boolean(
  task
  && (task.title || '').trim()
  && !(task.messageId && String(task.messageId).trim()),
);

export const isMappedChat = (task) => Boolean(
  task?.mapTaskId
  && String(task.mapTaskId).trim()
  && task.messageId
  && String(task.messageId).trim(),
);

const minutesOf = (value) => {
  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
};

export const isSubtaskFinished = (task) => minutesOf(task?.completionTimeInMinutes) > 0;

export const sumSubtaskActualMinutes = (subtasks = []) => (
  subtasks.reduce((sum, item) => sum + minutesOf(item.completionTimeInMinutes), 0)
);

export const isTaskTimeRecorded = (task, mappedItems = []) => {
  if (minutesOf(task?.completionTimeInMinutes) > 0) return true;
  const status = task?.status || task?.statusString;
  if (status === 'COMPLETED') return true;
  const subtasks = (mappedItems || []).filter(isNamedSubtask);
  return subtasks.length > 0 && subtasks.every(isSubtaskFinished);
};

export const taskRecordedMinutes = (task, mappedItems = []) => {
  const own = minutesOf(task?.completionTimeInMinutes);
  if (own > 0) return own;
  const subtasks = (mappedItems || []).filter(isNamedSubtask);
  if (subtasks.length > 0) return sumSubtaskActualMinutes(subtasks);
  return 0;
};

export const workflowLinkedTasksFrom = (items = []) => (
  (Array.isArray(items) ? items : []).filter(isWorkflowLinkedTask)
);

/** Auto journey task (same title as the workflow, or untitled) must not block close. */
export const blockingWorkflowLinkedTasks = (linkedTasks = [], workflowTitle = '') => {
  const list = Array.isArray(linkedTasks) ? linkedTasks : [];
  const title = String(workflowTitle || '').trim().toLowerCase();
  return list.filter((task) => {
    const taskTitle = String(task?.title || '').trim();
    if (!taskTitle) return false;
    if (title && taskTitle.toLowerCase() === title) return false;
    return true;
  });
};

export const unfinishedWorkflowLinkedCount = (linkedTasks = [], mappedByTaskId = {}, workflowTitle = '') => (
  blockingWorkflowLinkedTasks(linkedTasks, workflowTitle)
    .filter((task) => !isTaskTimeRecorded(task, mappedByTaskId[task.id] || [])).length
);

export const sumWorkflowLinkedMinutes = (linkedTasks = [], mappedByTaskId = {}) => (
  linkedTasks.reduce((sum, task) => sum + taskRecordedMinutes(task, mappedByTaskId[task.id] || []), 0)
);

export const workflowLinkedIncompleteMessage = (unfinishedCount) => (
  unfinishedCount === 1
    ? "Can't mark this workflow done until the linked task is completed"
    : `Can't mark this workflow done until all linked tasks are completed (${unfinishedCount} remaining)`
);

export const unfinishedSubtasksMessage = (unfinishedCount) => (
  unfinishedCount === 1
    ? "Can't complete this task until the remaining subtask is done"
    : `Can't complete this task until all subtasks are done (${unfinishedCount} remaining)`
);

export const unfinishedSubtasksError = (mappedItems = []) => {
  const subtasks = (Array.isArray(mappedItems) ? mappedItems : []).filter(isNamedSubtask);
  if (subtasks.length === 0) return null;
  const unfinishedCount = subtasks.filter((item) => !isSubtaskFinished(item)).length;
  if (unfinishedCount === 0) return null;
  return unfinishedSubtasksMessage(unfinishedCount);
};

export const apiErrorMessage = (err, fallback = 'Something went wrong') => {
  const pick = (value) => {
    if (typeof value === 'string' && value.trim()) {
      return value.replace(/^Error:\s*/i, '').trim();
    }
    return '';
  };
  const fromObject = (value) => {
    if (!value || typeof value !== 'object') return '';
    return pick(value.error) || pick(value.message) || pick(value.msg);
  };
  return pick(err) || fromObject(err) || fromObject(err?.payload) || fallback;
};

export const loadMappedByTaskId = async (tasks, loadMapped) => {
  const mappedByTaskId = {};
  await Promise.all((Array.isArray(tasks) ? tasks : []).map(async (task) => {
    if (!task?.id) return;
    try {
      const items = await loadMapped(task.id);
      mappedByTaskId[task.id] = Array.isArray(items) ? items : [];
    } catch {
      mappedByTaskId[task.id] = [];
    }
  }));
  return mappedByTaskId;
};

/**
 * If the workflow has linked +task items, they must all be finished first.
 * Workflow close time is not counted in productivity (tasks/reminders are).
 * Returns { minutes: 0 } or { minutes: null, error }.
 */
export const resolveWorkflowCloseMinutes = async ({
  linkedTasks = [],
  mappedByTaskId = {},
  workflowTitle = '',
}) => {
  const tasks = blockingWorkflowLinkedTasks(workflowLinkedTasksFrom(linkedTasks), workflowTitle);
  if (tasks.length === 0) {
    return { minutes: 0 };
  }
  const unfinishedCount = unfinishedWorkflowLinkedCount(tasks, mappedByTaskId, workflowTitle);
  if (unfinishedCount > 0) {
    return { minutes: null, error: workflowLinkedIncompleteMessage(unfinishedCount) };
  }
  return { minutes: 0 };
};
